/**
 * Public-card helpers for Instagram-sourced events.
 *
 * Primary signal: `event.instagramEvent` (boolean on the Firestore doc).
 * Legacy fallback: Instagram URL + placeholder/empty title (pre-flag events).
 */

const IG_PLACEHOLDER_TITLES = new Set([
  '',
  'external event link',
  'event link',
  'instagram',
  'poster',
  'event',
]);

function isPlaceholderTitle(title) {
  const t = String(title || '').trim().toLowerCase();
  if (IG_PLACEHOLDER_TITLES.has(t)) return true;
  if (/^https?:\/\//i.test(t)) return true;
  return false;
}

/** Instagram post/reel URL from dedicated field or poster link. */
export function resolveInstagramUrl(event) {
  const direct = String(event?.instagramUrl || '').trim();
  if (direct) return direct;
  const poster = String(event?.posterLink || '').trim();
  if (poster && /instagram\.com/i.test(poster)) return poster;
  return '';
}

function isLegacyInstagramOnlyEvent(event) {
  if (!resolveInstagramUrl(event)) return false;
  return isPlaceholderTitle(event?.title);
}

/**
 * Whether the public card should use Instagram-event chrome
 * (spectrum border, IG title styling). Outbound Instagram opens from
 * EventSingleContent sticky actions — cards open modal / detail page.
 *
 * `instagramEvent: true|false` wins; unset falls back to URL + placeholder title.
 */
export function isInstagramOnlyEvent(event) {
  if (!resolveInstagramUrl(event)) return false;
  if (event?.instagramEvent === true) return true;
  if (event?.instagramEvent === false) return false;
  return isPlaceholderTitle(event?.title);
}

/**
 * Admin Event Database checkbox state.
 * Explicit flag when set; otherwise mirrors legacy public detection.
 */
export function isInstagramEventChecked(event) {
  if (event?.instagramEvent === true) return true;
  if (event?.instagramEvent === false) return false;
  return isLegacyInstagramOnlyEvent(event);
}

/** Placeholder CTA shown when an IG event has no real title (SEO/aria fallback). */
export const INSTAGRAM_CTA_TITLE = 'click to find out more';

/** True when `title` is the IG "click to find out more" CTA (case-insensitive). */
export function isInstagramCtaTitle(title) {
  return String(title || '').trim().toLowerCase() === INSTAGRAM_CTA_TITLE;
}

export function getEventDisplayTitle(event) {
  const title = String(event?.title || '').trim();
  if (title && !isPlaceholderTitle(title)) return title;
  if (isInstagramOnlyEvent(event)) return INSTAGRAM_CTA_TITLE;
  if (title) return title;
  return 'Sarajevo event';
}
